import React, { useState } from "react";
import styles from "./PfpModal.module.css";

interface PfpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (url: string) => Promise<void>;
  donatorLevel: number;
}

const allowedExtensions = ["png", "jpg", "jpeg", "webp", "gif"];

export const PfpModal: React.FC<PfpModalProps> = ({ isOpen, onClose, onSubmit, donatorLevel }) => {
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const validate = (url: string): string | null => {
    if (!url) return "A valid pfp URL is required.";
    try {
      new URL(url);
    } catch {
      return "A valid pfp URL is required.";
    }
    const ext = url.split(".").pop()?.toLowerCase();
    if (!ext || !allowedExtensions.includes(ext)) {
      return "Unsupported image format. Use png, jpg, jpeg, webp, or gif.";
    }
    if (ext === "gif" && donatorLevel < 3) {
      return "Gold donator or above is required to set a gif profile picture.";
    }
    if (donatorLevel === 0) {
      return "Only donators can set a profile picture.";
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    const err = validate(url);
    if (err) {
      setError(err);
      return;
    }
    setLoading(true);
    try {
      await onSubmit(url);
      setSuccess(true);
      setUrl("");
    } catch (e: any) {
      setError(e.message || "Failed to update profile picture.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        <h2>Change Profile Picture</h2>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Image URL (png, jpg, jpeg, webp, gif)"
            value={url}
            onChange={e => setUrl(e.target.value)}
            disabled={loading}
            className={styles.input}
          />
          <button type="submit" className={styles.submitBtn} disabled={loading}>
            {loading ? "Updating..." : "Update"}
          </button>
        </form>
        {error && <div className={styles.error}>{error}</div>}
        {success && <div className={styles.success}>Profile picture updated!</div>}
        <div className={styles.info}>
          <b>Donator Level:</b> {donatorLevel === 0 ? "None" : donatorLevel === 1 ? "Bronze" : donatorLevel === 2 ? "Silver" : donatorLevel === 3 ? "Gold" : "Platinum"}<br />
          <b>Allowed formats:</b> png, jpg, jpeg, webp{donatorLevel >= 3 ? ", gif" : ""}
        </div>
      </div>
    </div>
  );
};
